import { memo } from 'react';
import type { ReactNode } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/shared/lib/cn';
import { Checkbox } from '@/shared/ui/checkbox';

export interface SelectableCardProps {
  selected: boolean;
  onToggle: () => void;
  index?: number;
  /** Rótulo para leitores de tela no checkbox. */
  ariaLabel: string;
  children: ReactNode;
  actions?: ReactNode;
  className?: string;
  /** Quando false, remove o checkbox. */
  selectable?: boolean;
  /**
   * Clique no corpo do card (fora do checkbox/ações).
   * Se omitido, usa `onToggle` quando `selectable`; `null` deixa o corpo sem clique.
   */
  onCardClick?: (() => void) | null;
  /** Fade na entrada. Listas que remontam a cada rota devem passar false. */
  enter?: boolean;
}

/** Card horizontal selecionável (padrão Leads / Banners / Produtos). */
export const SelectableCard = memo(function SelectableCard({
  selected,
  onToggle,
  index = 0,
  ariaLabel,
  children,
  actions,
  className,
  selectable = true,
  onCardClick,
  enter = true,
}: SelectableCardProps) {
  const stop = (e: React.MouseEvent) => e.stopPropagation();
  const handleClick = onCardClick === null ? undefined : (onCardClick ?? (selectable ? onToggle : undefined));

  return (
    <motion.div
      initial={enter ? { opacity: 0 } : false}
      animate={{ opacity: 1 }}
      transition={enter ? { delay: Math.min(index * 0.025, 0.3), duration: 0.2 } : { duration: 0 }}
      onClick={handleClick}
      className={cn(
        'glass group relative flex items-start gap-3 rounded-2xl p-3.5 pr-3 transition-[border-color,background-color,box-shadow] duration-200 sm:items-center sm:gap-4',
        handleClick && 'cursor-pointer',
        selected && selectable && 'border-primary/40 bg-primary/[0.06] ring-1 ring-primary/35',
        className,
      )}
    >
      {selectable ? (
        <Checkbox
          checked={selected}
          onCheckedChange={() => onToggle()}
          onClick={stop}
          aria-label={ariaLabel}
          className="mt-1 sm:mt-0"
        />
      ) : null}
      <div className="min-w-0 flex-1">{children}</div>
      {actions ? (
        <div
          className="flex shrink-0 items-start justify-end gap-1 sm:items-center"
          onClick={stop}
        >
          {actions}
        </div>
      ) : null}
    </motion.div>
  );
});
